import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, ExternalLink, ShoppingBag } from "lucide-react";
import { api, errMsg, fmtDate, ghs } from "../../lib/api";
import { EmptyState, PageHeader, StatusBadge } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";

export function Purchases() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/me/purchases").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="space-y-10">
      <PageHeader eyebrow="Library" title="My Purchases" text="Everything you've bought, available any time." />
      {items === null ? <FullLoader /> : items.length === 0 ? (
        <EmptyState icon={ShoppingBag} testId="purchases-empty" title="No purchases yet" text="Products you buy will appear here instantly after payment." action={<Link to="/store" className="btn-primary">Browse products</Link>} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="purchases-list">
          {items.map((p) => (
            <Link key={p.id} to={`/purchases/${p.id}`} className="card-rx card-hover flex items-center justify-between gap-4 p-5" data-testid={`purchase-item-${p.id}`}>
              <div>
                <p className="font-semibold text-white">{p.item_name}</p>
                <p className="mt-1 text-xs text-slate-500">{p.item_type === "product" ? "Digital product" : "Prediction package"} · {fmtDate(p.created_at)}</p>
              </div>
              <span className="font-mono-rx text-sm text-amber-400">{ghs(p.amount_minor)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PurchaseDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/me/purchases/${id}`).then((r) => setD(r.data)).catch(() => setMissing(true)); }, [id]);

  const download = async () => {
    try {
      const r = await api.get(`/me/purchases/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: d.delivery.file_name || "download" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "Download failed")); }
  };

  if (missing) return <EmptyState title="Purchase not found" action={<Link to="/purchases" className="btn-ghost">Back</Link>} />;
  if (!d) return <FullLoader />;
  const { purchase: p, delivery } = d;
  const safeLink = delivery?.type === "link" && /^https?:\/\//i.test(delivery.value);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <Link to="/purchases" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={16} /> My Purchases</Link>
      <div className="card-rx p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h1 className="text-2xl font-bold text-white" data-testid="purchase-detail-name">{p.item_name}</h1><p className="mt-1 font-mono-rx text-xs text-slate-500">Ref {p.transaction_ref} · {fmtDate(p.created_at, true)}</p></div>
          <StatusBadge status="success" label="Paid" />
        </div>
        <div className="mt-8 border-t border-white/[0.06] pt-6" data-testid="purchase-delivery">
          {p.item_type !== "product" ? (
            <p className="text-sm text-slate-300">Access active until <b>{fmtDate(d.access?.expires_at, true)}</b>. Your prediction content will appear in the GET PREDICTIONS NOW section once it's published.</p>
          ) : !delivery ? <p className="text-sm text-slate-400">Product details are unavailable.</p> : (
            <div className="space-y-4">
              <p className="eyebrow">Your product</p>
              {delivery.type === "text" && <p className="whitespace-pre-line rounded-xl bg-white/[0.03] p-4 text-sm text-slate-200">{delivery.value || "The seller will share access details shortly."}</p>}
              {safeLink && <a href={delivery.value} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="purchase-open-link-btn"><ExternalLink size={16} /> Open product</a>}
              {delivery.type === "link" && !safeLink && <p className="text-sm text-slate-400">Access link will be provided by support.</p>}
              {delivery.has_file && <button onClick={download} className="btn-primary" data-testid="purchase-download-btn"><Download size={16} /> Download {delivery.file_name}</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
